import React, { useState } from "react";
import {
  Add,
  CheckCircle,
  CheckCircleOutline,
  MarkEmailRead,
  MarkEmailUnread,
  OpenInNew,
  RateReview,
  RateReviewOutlined,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Select,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import {
  formatFloatToTimeHM,
  parseTimeHMToFloat,
} from "../../utils/timeFormatter";
import { typesForCategory } from "../../utils/constants";
import { itemKey } from "../../hooks/useActionItems";

const IconGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  alignItems: "center",
  justifyContent: "flex-start",
  // The four action icons are a single unit: wrapping them pushed the last one
  // onto its own line and made the row twice as tall.
  flexWrap: "nowrap",
  "& .MuiIconButton-root": { flexShrink: 0 },
}));

const StyledIconButton = styled(IconButton)(({ theme, active }) => ({
  padding: "4px",
  color: active ? theme.palette.secondary.main : theme.palette.action.disabled,
  "&:hover": {
    color: active ? theme.palette.secondary.dark : theme.palette.action.active,
    backgroundColor: theme.palette.action.hover,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
}));

const CustomTextBox = styled(Typography)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.text.secondary}`,
  paddingTop: 0.8,
  minHeight: "2rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
}));

// Column widths as flex rules rather than percentages: every column that holds
// content of a known size gets a fixed px width so it can never wrap, and only
// Action Items / Notes absorb the leftover space. The compact set is used inside
// the ~60% detail drawer. When even the minimums don't fit, the row scrolls
// sideways instead of stacking (see ROW_MIN_WIDTH).
const WIDE_COLS = {
  actions: { flex: "0 0 132px" },
  lineUp: { flex: "0 0 52px" },
  items: { flex: "1 1 0", minWidth: 140 },
  type: { flex: "0 0 124px" },
  notes: { flex: "1 1 0", minWidth: 120 },
  est: { flex: "0 0 84px" },
  taken: { flex: "0 0 84px" },
};
const COMPACT_COLS = {
  actions: { flex: "0 0 116px" },
  lineUp: { flex: "0 0 44px" },
  items: { flex: "1 1 0", minWidth: 110 },
  type: { flex: "0 0 104px" },
  notes: { flex: "1 1 0", minWidth: 90 },
  est: { flex: "0 0 72px" },
  taken: { flex: "0 0 72px" },
};

// Sum of the fixed widths, the flexible minimums and the gaps, so the row keeps
// its shape and the container scrolls once it gets narrower than this.
const WIDE_MIN_WIDTH = 740;
const COMPACT_MIN_WIDTH = 630;

/**
 * The action-item table shared by the task create dialog and the task detail
 * page/drawer, so the columns, order and controls are identical everywhere.
 *
 * @param {Object} props
 * @param {Array} props.actionItems Items to render (saved items carry `id`,
 *   unsaved ones carry `tempId`).
 * @param {string} [props.category] The parent task's category. It decides which
 *   Types the per-item Type picker offers; without one the picker is empty.
 * @param {(key: number|string, field: string, value: any) => void} props.onUpdateItem
 *   `key` is the row's `id`, or its `tempId` while it is still a draft. Drafts
 *   are addressed by key rather than as "the pending item" so edits still land
 *   on the right row when more than one draft is open.
 * @param {() => void} [props.onAddItem] Omit to hide the add row.
 * @param {(item: Object) => void} [props.onOpenItem]
 * @param {(event: React.MouseEvent, item: Object) => void} [props.onContextMenu]
 * @param {boolean} [props.compact] Use the narrow drawer column widths.
 * @param {boolean} [props.disabled] Dim and block interaction (task not created yet).
 */
const ActionItemsTable = ({
  actionItems = [],
  category,
  onUpdateItem,
  onAddItem,
  onOpenItem,
  onContextMenu,
  compact = false,
  disabled = false,
}) => {
  // Estimated time is typed as free text ("1:30") and only parsed on blur, so
  // the raw keystrokes are held here until then.
  const [timeInputStrings, setTimeInputStrings] = useState({});

  const COLS = compact ? COMPACT_COLS : WIDE_COLS;
  const ROW_GAP = compact ? 0.5 : 1;
  const MIN_WIDTH = compact ? COMPACT_MIN_WIDTH : WIDE_MIN_WIDTH;

  // Shared by the header and every body row so the columns stay aligned.
  const rowSx = {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: ROW_GAP,
    py: 0.5,
    px: 1,
    minWidth: MIN_WIDTH,
  };

  const update = (item, field, value) => onUpdateItem(itemKey(item), field, value);

  // Types on offer come from the parent task's category; a legacy value already
  // on the row is kept so switching category doesn't silently blank the cell.
  const itemTypes = (currentType) => typesForCategory(category, currentType);

  const header = (label, col) => (
    <Box sx={col}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <Paper
      elevation={1}
      sx={{
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.5 : 1,
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: MIN_WIDTH }}>
        <Box
          sx={{
            ...rowSx,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {header("Actions", COLS.actions)}
          {header("Line up", COLS.lineUp)}
          {header("Action Items", COLS.items)}
          {header("Type", COLS.type)}
          {header("Notes", COLS.notes)}
          {header("Estimated", COLS.est)}
          {header("Time Taken", COLS.taken)}
        </Box>

        {actionItems.map((actionItem, index) => {
          const key = itemKey(actionItem) || `new-${index}`;
          const estString =
            timeInputStrings[key] !== undefined
              ? timeInputStrings[key]
              : formatFloatToTimeHM(actionItem.ai_time_estimate);
          const estInvalid = estString.trim() === "" || estString === "00:00";

          return (
            <Box
              key={key}
              sx={{ ...rowSx, "&:hover": { bgcolor: "action.hover" } }}
              onContextMenu={(e) => onContextMenu && onContextMenu(e, actionItem)}
            >
              <Box sx={COLS.actions}>
                <IconGroup>
                  <StyledIconButton
                    size="small"
                    active={!actionItem.read}
                    onClick={() => update(actionItem, "read", !actionItem.read)}
                  >
                    {actionItem.read ? <MarkEmailRead /> : <MarkEmailUnread />}
                  </StyledIconButton>

                  <StyledIconButton
                    size="small"
                    active={actionItem.is_completed}
                    onClick={() =>
                      update(actionItem, "is_completed", !actionItem.is_completed)
                    }
                  >
                    {actionItem.is_completed ? (
                      <CheckCircle />
                    ) : (
                      <CheckCircleOutline />
                    )}
                  </StyledIconButton>

                  <StyledIconButton
                    size="small"
                    active={actionItem.is_reviewed}
                    disabled={!actionItem.is_completed}
                    onClick={() =>
                      update(actionItem, "is_reviewed", !actionItem.is_reviewed)
                    }
                  >
                    {actionItem.is_reviewed ? (
                      <RateReview />
                    ) : (
                      <RateReviewOutlined />
                    )}
                  </StyledIconButton>

                  <StyledIconButton
                    size="small"
                    disabled={!actionItem.id || !onOpenItem}
                    onClick={() => onOpenItem && onOpenItem(actionItem)}
                  >
                    <OpenInNew />
                  </StyledIconButton>
                </IconGroup>
              </Box>

              <Box sx={COLS.lineUp}>
                <TextField
                  fullWidth
                  variant="standard"
                  type="number"
                  value={actionItem.seq ?? ""}
                  onChange={(e) =>
                    update(
                      actionItem,
                      "seq",
                      Math.max(1, parseInt(e.target.value, 10) || 1)
                    )
                  }
                />
              </Box>

              <Box sx={COLS.items}>
                <TextField
                  fullWidth
                  variant="standard"
                  value={actionItem.description || ""}
                  error={!actionItem.description}
                  onChange={(e) =>
                    update(actionItem, "description", e.target.value)
                  }
                />
              </Box>

              <Box sx={COLS.type}>
                <Select
                  fullWidth
                  displayEmpty
                  variant="standard"
                  disabled={!category}
                  value={actionItem.type || ""}
                  onChange={(e) => update(actionItem, "type", e.target.value)}
                  renderValue={(value) =>
                    value || (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    )
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {itemTypes(actionItem.type).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={COLS.notes}>
                <TextField
                  fullWidth
                  variant="standard"
                  value={actionItem.notes || ""}
                  onChange={(e) => update(actionItem, "notes", e.target.value)}
                />
              </Box>

              <Box sx={COLS.est}>
                <TextField
                  fullWidth
                  variant="standard"
                  type="text"
                  value={estString}
                  error={estInvalid}
                  onChange={(e) =>
                    setTimeInputStrings((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  onBlur={() => {
                    const inputStr = timeInputStrings[key];
                    if (inputStr === undefined) return;

                    const trimmed = inputStr.trim();
                    let valueToSave = actionItem.ai_time_estimate;
                    if (trimmed === "") {
                      valueToSave = null;
                    } else {
                      const parsed = parseTimeHMToFloat(trimmed);
                      if (!isNaN(parsed)) {
                        valueToSave = parsed;
                      } else {
                        const direct = parseFloat(trimmed);
                        if (!isNaN(direct) && String(direct) === trimmed) {
                          valueToSave = direct;
                        }
                      }
                    }

                    if (valueToSave !== actionItem.ai_time_estimate) {
                      update(actionItem, "ai_time_estimate", valueToSave);
                    }
                    setTimeInputStrings((prev) => {
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    });
                  }}
                />
              </Box>

              <Box sx={COLS.taken}>
                <CustomTextBox sx={{ whiteSpace: "nowrap" }}>
                  {formatFloatToTimeHM(actionItem.time_taken)}
                </CustomTextBox>
              </Box>
            </Box>
          );
        })}

        {onAddItem && (
          <Box
            onClick={onAddItem}
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1,
              px: 1,
              cursor: "pointer",
              borderTop: 1,
              borderColor: "divider",
              color: "secondary.main",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Add fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Add Action Item
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ActionItemsTable;
